import { h, $, type Child } from './dom';
import { layers } from './layers';
import { m, onLangChange } from '../i18n';
import { sfx } from '../audio/sfx';

export interface PanelSpec {
  title: string;
  body: Child;
  /** Extra footer buttons; a Close button is always added. */
  footer?: Node[];
  className?: string;
  closable?: boolean;
}

interface OpenPanel {
  el: HTMLElement;
  render: () => PanelSpec;
  onClose?: () => void;
}

const panels = new Map<string, OpenPanel>();

/**
 * Open (or re-render) a modal panel. `render` is called again on language change
 * and whenever `refreshPanel(id)` is called, so panels never show stale text.
 */
export function openPanel(id: string, render: () => PanelSpec, onClose?: () => void): void {
  let p = panels.get(id);
  if (!p) {
    const el = h('div', { class: 'panel', role: 'dialog', 'aria-modal': 'true' });
    el.addEventListener('pointerdown', (e) => {
      if (e.target === el && render().closable !== false) closePanel(id);
    });
    $('panels').appendChild(el);
    p = { el, render, onClose };
    panels.set(id, p);
    layers.open(`panel:${id}`);
    requestAnimationFrame(() => el.classList.add('show'));
  } else {
    p.render = render;
    p.onClose = onClose ?? p.onClose;
  }
  paint(id);
}

function paint(id: string): void {
  const p = panels.get(id);
  if (!p) return;
  const spec = p.render();
  const box = h(
    'div',
    { class: `panel-box ${spec.className ?? ''}` },
    h('h3', null, spec.title),
    h('div', { class: 'panel-body' }, spec.body),
    h(
      'div',
      { class: 'panel-foot' },
      spec.footer ?? [],
      spec.closable === false ? null : h('button', { class: 'btn ghost', onclick: () => (sfx('click'), closePanel(id)) }, m().common.close),
    ),
  );
  const prev = p.el.querySelector('.panel-body');
  const scroll = prev?.scrollTop ?? 0;
  p.el.replaceChildren(box);
  box.querySelector('.panel-body')!.scrollTop = scroll;
}

export function refreshPanel(id: string): void {
  paint(id);
}

export function closePanel(id: string): void {
  const p = panels.get(id);
  if (!p) return;
  panels.delete(id);
  p.el.classList.remove('show');
  window.setTimeout(() => p.el.remove(), 180);
  layers.close(`panel:${id}`);
  p.onClose?.();
}

export function isPanelOpen(id: string): boolean {
  return panels.has(id);
}

/** Close the topmost panel (Esc). Returns true if one was closed. */
export function closeTopPanel(): boolean {
  const ids = Array.from(panels.keys());
  const id = ids[ids.length - 1];
  if (!id) return false;
  const p = panels.get(id)!;
  if (p.render().closable === false) return false;
  closePanel(id);
  return true;
}

onLangChange(() => panels.forEach((_, id) => paint(id)));
